import * as React from "react";
import { LinksProps } from "./typings";
import { UitkCard, UitkCardContent, UitkCardSectionSeparatorType, UitkCardTextSegment } from "uitk-react-cards";
import { UitkIcon } from "uitk-react-icons";
import { UitkLink } from "uitk-react-link";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { DefaultFlexModuleResult, Link } from "typings/microserviceModels/links-flex-module";
import { LocalizedText } from "bernie-l10n";
import { GeoType } from "src/typings/flexFramework/FlexDefinitions";
import { LocationType } from "src/components/utility/LocationUtils";
import { InsideTabGroupContext } from "components/shared/InsideTabGroupContext/InsideTabGroupContext";
import { UitkLayoutGrid, UitkLayoutGridColumnRow, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkHeading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { SpacingSpaces } from "uitk-react-spacing/dist/types";
import { observer } from "mobx-react";
import { withStores } from "src/stores";
import { normalizeOptionsData } from "src/stores/FlexTemplateStore";
import { LazyLoad } from "src/components/utility/LazyLoad/LazyLoad";

const getUitkLink = (model: DefaultFlexModuleResult, el: Link) => {
  const text = `${el.text} ${(el.auxiliaryText && `(${el.auxiliaryText})`) || ""}`;

  if (!el.url) {
    return <span className="uitk-link-layout-default uitk-type-300">{text}</span>;
  }

  return (
    <UitkLink>
      <TrackedLink moduleName={model.name} href={el.url} rfrr={model.strategy} linkName={el.text}>
        {text}
      </TrackedLink>
    </UitkLink>
  );
};

const SeeAllContentLink = ({
  model,
  location,
  locationType,
}: {
  model: DefaultFlexModuleResult;
  location: string;
  locationType?: GeoType;
}) => {
  const useNearPreposition =
    locationType &&
    (locationType === LocationType.AIRPORT ||
      locationType === LocationType.POINT_OF_INTEREST ||
      locationType === LocationType.METRO_STATION ||
      locationType === LocationType.TRAIN_STATION);

  const linksMessage = useNearPreposition ? "links.seeAllHotelsNear" : "links.seeAllHotels";

  return (
    <UitkLink>
      <TrackedLink
        moduleName={model.name}
        href={model.seeAllHotelLinks}
        rfrr={model.strategy}
        linkName={model.seeAllHotelLinks}
      >
        <LocalizedText message={linksMessage} data={[location]} />
      </TrackedLink>
    </UitkLink>
  );
};

/**
 * Card with links. Handled three different ways. Either it will display
 * auxiliary content in parenthesis, with an additional card to see more info,
 * or none of the former.
 *
 * Mock flexComponent prop received: @see {.offline/viewModels/Links.json}
 *
 */
export const Links = withStores(
  "flexModuleModelStore",
  "context"
)(
  observer((props: LinksProps) => {
    const { flexTemplateNode, flexModuleModelStore, insideTabGroupParam, context } = props;
    const { id } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as DefaultFlexModuleResult | null;
    const options = normalizeOptionsData(flexTemplateNode, model);
    const [toggle, setToggle] = React.useState(model?.toggle === "collapsed");
    let insideTabGroup = React.useContext(InsideTabGroupContext);
    insideTabGroup = insideTabGroupParam || insideTabGroup;
    if (!model || !model.links || model.links.length === 0) {
      return null;
    }

    const DEFAULT_MOBILE_DISPLAY_COLUMNS = 1;

    const displayColumns = Number(model.displayColumns) as UitkLayoutGridColumnRow;

    const location = model.locationName || context.searchContext.location?.name;
    const locationType = model.locationType || context.searchContext.location?.type;
    const keyHelper = new ItemKeyHelper("links");
    const containerPadding: SpacingSpaces = insideTabGroup ? {} : { blockstart: "three" };
    const tabPanelPadding: SpacingSpaces =
      toggle && !insideTabGroup ? { inline: "three", block: "three" } : { inline: "four", block: "two" };

    const flipToggle = (): void => {
      setToggle(!toggle);
    };

    return (
      <LazyLoad context={context}>
        <UitkSpacing padding={containerPadding}>
          <div
            className="Links uitk-cell all-cell-1-1"
            id={id}
            data-fm={options.fmId}
            data-fm-title-id={options.fmTitleId}
          >
            <UitkCard>
              <UitkCardContent>
                {!insideTabGroup && (
                  <div role="button" aria-expanded={!toggle} className="header" onClick={flipToggle}>
                    <UitkCardTextSegment
                      className={`uitk-flex uitk-flex-align-items-center  ${toggle && "allBorderZero"}`}
                      separator={UitkCardSectionSeparatorType.BOTTOM}
                    >
                      <UitkHeading tag="h3" size={5}>
                        {model.title}
                      </UitkHeading>
                      <UitkIcon name={`expand_${toggle ? "more" : "less"}`} />
                    </UitkCardTextSegment>
                  </div>
                )}

                <div className={`uitk-cell ${toggle && !insideTabGroup ? "is-visually-hidden" : ""}`}>
                  {insideTabGroup && (
                    <UitkSpacing margin={{ inline: "three" }}>
                      <UitkHeading tag="h3" size={5}>
                        {model.title}
                      </UitkHeading>
                    </UitkSpacing>
                  )}
                  <UitkSpacing padding={tabPanelPadding}>
                    <UitkLayoutGrid
                      tag="ul"
                      space="one"
                      className="no-bullet"
                      columns={{ small: DEFAULT_MOBILE_DISPLAY_COLUMNS, large: displayColumns }}
                    >
                      {model.links.map((el) => (
                        <UitkLayoutGridItem colSpan={1} key={keyHelper.next()}>
                          <li>{getUitkLink(model, el)}</li>
                        </UitkLayoutGridItem>
                      ))}
                    </UitkLayoutGrid>
                  </UitkSpacing>
                  {model.seeAllHotelLinks && location && (
                    <UitkCardTextSegment separator={UitkCardSectionSeparatorType.TOP} className="seeAllSection">
                      <SeeAllContentLink model={model} location={location} locationType={locationType} />
                    </UitkCardTextSegment>
                  )}
                </div>
              </UitkCardContent>
            </UitkCard>
          </div>
        </UitkSpacing>
      </LazyLoad>
    );
  })
);

export default Links;
